// public/js/pages/users.js
import { can, getCurrentUser, PLATFORM_ROLES } from '../modules/rbac.js';
import { loadUsers, updateUserGroups, createUser, deleteUser, userAdminAction, notifyAccessChange } from '../modules/users.js';
import { loadTenants, assignUserToTenant, unassignUserFromTenant } from '../modules/tenants.js';
import { tName } from '../modules/demo-mode.js';
import { scopeTenantParam } from '../modules/tenant-scope.js';

export async function initUsersPage(container) {
  const currentUser = getCurrentUser();
  if (!can(currentUser.groups || [], 'listUsers')) {
    container.innerHTML = `<div class="alert alert-danger">Access denied.</div>`;
    return;
  }

  container.innerHTML = `
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="h4 mb-0">User Management <span class="badge bg-primary ms-2" id="user-count">0</span></h2>
      <div class="d-flex gap-2">
        <button id="btn-add-user" class="btn btn-primary btn-sm d-none">
          <i class="fa-solid fa-user-plus"></i> Add user
        </button>
        <button id="btn-refresh-users" class="btn btn-outline-primary btn-sm">
          <i class="fa-solid fa-arrows-rotate"></i> Refresh
        </button>
      </div>
    </div>

    <div class="card shadow-sm">
      <div class="card-body border-bottom py-2">
        <div class="row g-2">
          <div class="col-12 col-md-4">
            <input id="uf-search" type="search" class="form-control form-control-sm" placeholder="Search name or email…">
          </div>
          <div class="col-4 col-md-2">
            <select id="uf-role" class="form-select form-select-sm"><option value="">All roles</option></select>
          </div>
          <div class="col-4 col-md-3">
            <select id="uf-tenant" class="form-select form-select-sm tenant-blur"><option value="">All tenants</option></select>
          </div>
          <div class="col-4 col-md-3">
            <select id="uf-status" class="form-select form-select-sm"><option value="">All statuses</option></select>
          </div>
        </div>
      </div>
      <div class="card-body p-0 table-responsive">
        <table class="table table-hover mb-0" id="users-table">
          <thead>
            <tr>
              <th class="u-sort" data-sort="name" style="cursor:pointer">Username<span class="sort-ind"></span></th>
              <th class="u-sort" data-sort="email" style="cursor:pointer">Email<span class="sort-ind"></span></th>
              <th>Tenants</th>
              <th>Roles / Groups</th>
              <th class="u-sort" data-sort="status" style="cursor:pointer">Status<span class="sort-ind"></span></th>
              <th class="u-sort" data-sort="lastSeen" style="cursor:pointer">Last login<span class="sort-ind"></span></th>
              <th class="u-sort" data-sort="created" style="cursor:pointer">Created<span class="sort-ind"></span></th>
              <th style="width: 60px;"></th>
            </tr>
          </thead>
          <tbody></tbody>
        </table>
      </div>
    </div>
  `;

  const tbody = container.querySelector('tbody');
  const countBadge = document.getElementById('user-count');

  // ── list state: loaded users + client-side filter & sort ──────────────────
  let allUsers = [];
  let sortKey = 'created';   // default: newest users on top
  let sortDir = 'desc';

  const relTime = (iso) => {
    if (!iso) return '—';
    const s = (Date.now() - new Date(iso).getTime()) / 1000;
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)} min ago`;
    if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
    if (s < 7 * 86400) return `${Math.floor(s / 86400)} d ago`;
    return new Date(iso).toLocaleDateString(navigator.language);
  };

  const sortValue = (u, key) => {
    switch (key) {
      case 'name':     return (u.email ? u.email.split('@')[0] : (u.username || '')).toLowerCase();
      case 'email':    return (u.email || '').toLowerCase();
      case 'status':   return `${u.enabled ? 'a' : 'z'}${u.status || ''}`;
      case 'lastSeen': return u.lastSeen || '';
      case 'created':  return u.created || '';
      default:         return '';
    }
  };

  function filteredUsers() {
    const q = (container.querySelector('#uf-search')?.value || '').trim().toLowerCase();
    const role = container.querySelector('#uf-role')?.value || '';
    const tenant = container.querySelector('#uf-tenant')?.value || '';
    const status = container.querySelector('#uf-status')?.value || '';

    let list = allUsers.filter(u => {
      if (q && !`${u.email || ''} ${u.username || ''}`.toLowerCase().includes(q)) return false;
      if (role === '__none' ? (u.groups || []).length : (role && !(u.groups || []).includes(role))) return false;
      if (tenant && !(u.tenants || []).some(t => (t.tenantId || t) === tenant)) return false;
      if (status === '__disabled' ? u.enabled : (status && u.status !== status)) return false;
      return true;
    });

    list.sort((a, b) => {
      const va = sortValue(a, sortKey), vb = sortValue(b, sortKey);
      // Empty values (e.g. never logged in) always sort to the bottom.
      if (va !== vb && (va === '' || vb === '')) return va === '' ? 1 : -1;
      const cmp = va < vb ? -1 : (va > vb ? 1 : 0);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }

  // Build filter options from the loaded list (only values that exist).
  function populateFilterOptions() {
    const roleSel = container.querySelector('#uf-role');
    const tenantSel = container.querySelector('#uf-tenant');
    const statusSel = container.querySelector('#uf-status');
    const keep = (sel) => sel.value;

    const roles = [...new Set(allUsers.flatMap(u => u.groups || []))].sort();
    const prevRole = keep(roleSel);
    roleSel.innerHTML = '<option value="">All roles</option>'
      + roles.map(r => `<option value="${esc(r)}">${esc(r)}</option>`).join('')
      + '<option value="__none">No role</option>';
    roleSel.value = prevRole;

    const tenantMap = new Map();
    allUsers.forEach(u => (u.tenants || []).forEach(t => tenantMap.set(t.tenantId || t, t.name || t.tenantId || t)));
    const prevTenant = keep(tenantSel);
    tenantSel.innerHTML = '<option value="">All tenants</option>'
      + [...tenantMap.entries()].sort((a, b) => String(a[1]).localeCompare(String(b[1])))
        .map(([id, name]) => `<option value="${esc(id)}">${esc(name)}</option>`).join('');
    tenantSel.value = prevTenant;

    // Global tenant scope: under a single scope the list is pinned to that
    // tenant and the per-page tenant select disappears (docs/tenant-scope.md).
    const scopeTid = scopeTenantParam();
    if (scopeTid && scopeTid !== 'unassigned') {
      if ([...tenantMap.keys()].includes(scopeTid)) tenantSel.value = scopeTid;
      tenantSel.closest('div')?.classList.add('d-none');
    } else {
      tenantSel.closest('div')?.classList.remove('d-none');
    }

    const statuses = [...new Set(allUsers.map(u => u.status).filter(Boolean))].sort();
    const prevStatus = keep(statusSel);
    statusSel.innerHTML = '<option value="">All statuses</option>'
      + statuses.map(s => `<option value="${esc(s)}">${esc(s)}</option>`).join('')
      + '<option value="__disabled">Disabled</option>';
    statusSel.value = prevStatus;
  }

  function renderRows() {
    const users = filteredUsers();
    countBadge.textContent = allUsers.length === users.length
      ? allUsers.length : `${users.length}/${allUsers.length}`;

    // Sort indicators on the active column.
    container.querySelectorAll('th.u-sort').forEach(th => {
      th.querySelector('.sort-ind').textContent = th.dataset.sort === sortKey ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
    });

    tbody.innerHTML = users.map(u => `
        <tr>
          <td><strong>${u.email ? u.email.split('@')[0] : '—'}</strong></td>
          <td>
            ${u.email
    ? `<span title="UUID: ${esc(u.sub)}" style="cursor:help; border-bottom: 1px dotted #aaa;">${esc(u.email)}</span>`
    : '<span class="text-muted">—</span>'}
          </td>
          <td>
          ${u.tenants && u.tenants.length > 0
    ? u.tenants.map(t => {
      // t may be a UUID or {tenantId, name}
      const name = typeof t === 'object' && t.name ? t.name : t;
      return `<span class="badge bg-info me-1">${tName(name)}</span>`;
    }).join('')
    : '<span class="text-muted">—</span>'}
          </td>
          <td>
            ${u.groups.length
    ? u.groups.map(g => `<span class="badge bg-secondary me-1">${esc(g)}</span>`).join('')
    : '<span class="badge bg-warning text-dark">No role assigned</span>'}
          </td>
          <td><span class="badge ${u.enabled ? 'bg-success' : 'bg-secondary'}">${u.enabled ? u.status : 'DISABLED'}</span>${
  u.emailVerified === false
    ? '<span class="badge bg-warning text-dark ms-1" title="No password reset can be sent to this address - neither by the user nor by an admin. Use Verify email address.">Email unverified</span>'
    : ''}</td>
          <td class="text-nowrap" ${u.lastSeen ? `title="${new Date(u.lastSeen).toLocaleString(navigator.language)}"` : ''}>${relTime(u.lastSeen)}</td>
          <td class="text-nowrap" ${u.created ? `title="${new Date(u.created).toLocaleString(navigator.language)}"` : ''}>${u.created ? new Date(u.created).toLocaleDateString(navigator.language) : '—'}</td>
          <td>${buildUserMenu(u)}</td>
        </tr>
      `).join('');

    bindUserMenus();
  }

  // Build the action menu for one row as a NATIVE Bootstrap dropdown: it
  // closes other open menus automatically, works with keyboard/touch, and
  // Popper handles the viewport (flip above the trigger at the bottom edge).
  function buildUserMenu(u) {
    const myGroups = currentUser.groups || [];
    const isSelf = u.username === currentUser.username || (u.sub && u.sub === currentUser.sub);
    // Full user admin (platform): any role, any user, full account lifecycle.
    const fullUserAdmin = can(myGroups, 'manageUserGroups');
    // Tenant-scoped admin: password + non-platform roles for OWN-tenant users.
    // Platform-level target users are off-limits to them (server enforces this too).
    const targetIsPlatform = (u.groups || []).some(g => PLATFORM_ROLES.includes(g));
    const tenantUserAdmin = can(myGroups, 'manageTenantUsers') && !targetIsPlatform;
    const items = [];
    // Role + tenant management — shown to full admins, tenant-user admins, or
    // anyone who can manage tenant memberships. (The role editor inside hides
    // platform roles for non-platform callers.)
    if (fullUserAdmin || tenantUserAdmin || can(myGroups, 'manageUserTenants')) {
      items.push('<li><a class="dropdown-item manage-user" href="#">🔐 Manage role &amp; tenants</a></li>');
    }
    // Password actions — full admins OR tenant-scoped admins (own-tenant users).
    if (fullUserAdmin || tenantUserAdmin) {
      if (u.status === 'CONFIRMED') items.push('<li><a class="dropdown-item user-reset-pw" href="#">🔑 Reset password (email)</a></li>');
      // Set a password directly — no email needed (activates the account).
      if (u.status === 'FORCE_CHANGE_PASSWORD' || u.status === 'CONFIRMED') {
        items.push('<li><a class="dropdown-item user-set-pw" href="#">🔓 Set password / activate…</a></li>');
      }
    }
    // Account lifecycle (confirm / resend / enable-disable / delete) — full
    // (platform) user admins only.
    if (fullUserAdmin) {
      if (u.status === 'UNCONFIRMED') items.push('<li><a class="dropdown-item user-confirm" href="#">✅ Confirm account</a></li>');
      // Only offered where it is actually broken: an unverified address blocks
      // BOTH password-reset paths, and nothing else in this list repairs it.
      if (u.emailVerified === false && u.status !== 'UNCONFIRMED') {
        items.push('<li><a class="dropdown-item user-verify-email" href="#">📧 Verify email address</a></li>');
      }
      if (u.status === 'FORCE_CHANGE_PASSWORD') items.push('<li><a class="dropdown-item user-resend" href="#">✉️ Resend invitation</a></li>');
      if (!isSelf) {
        items.push(`<li><a class="dropdown-item user-toggle-enabled" href="#">${u.enabled ? '🚫 Disable user' : '▶️ Enable user'}</a></li>`);
        items.push('<li><hr class="dropdown-divider"></li>');
        items.push('<li><a class="dropdown-item text-danger user-delete" href="#">🗑️ Delete user…</a></li>');
      }
    }
    if (!items.length) items.push('<li><span class="dropdown-item disabled">No actions available</span></li>');
    return `
      <div class="dropdown user-menu" data-username="${esc(u.username)}">
        <button type="button" class="btn btn-sm btn-link text-dark px-2" data-bs-toggle="dropdown" aria-expanded="false" aria-label="User actions">⋮</button>
        <ul class="dropdown-menu dropdown-menu-end shadow" style="min-width:220px;">${items.join('')}</ul>
      </div>`;
  }

  function bindUserMenus() {
    tbody.querySelectorAll('.user-menu').forEach(wrap => {
      const user = allUsers.find(x => x.username === wrap.dataset.username);
      if (!user) return;
      // Fixed strategy: escapes the .table-responsive overflow clipping and
      // lets Popper flip/shift the menu inside the viewport (mobile bottom rows).
      bootstrap.Dropdown.getOrCreateInstance(wrap.querySelector('[data-bs-toggle="dropdown"]'), {
        popperConfig: (cfg) => ({ ...cfg, strategy: 'fixed' }),
      });

      const on = (sel, handler) => wrap.querySelector(sel)?.addEventListener('click', (ev) => { ev.preventDefault(); handler(ev); });
      // One-shot lifecycle actions: run, toast the outcome, refresh the list.
      const act = (fn, okMsg) => async () => {
        try {
          await fn();
          window.showToast(okMsg, 'success');
          await renderUsers();
        } catch (e2) {
          window.showToast(e2?.message || 'Action failed', 'danger');
        }
      };
      on('.manage-user', () => openUserModal(user));
      on('.user-confirm', act(() => userAdminAction(user.username, 'confirm'), `Account confirmed for ${displayName(user)}`));
      on('.user-resend', act(() => userAdminAction(user.username, 'resend-invite'), `Invitation re-sent to ${user.email || displayName(user)}`));
      on('.user-verify-email', act(
        () => userAdminAction(user.username, 'verify-email'),
        `${user.email || displayName(user)} marked as verified - password reset works now`));
      on('.user-reset-pw', () => {
        if (!confirm(`Send a password-reset code to ${user.email || displayName(user)}? The current password stops working.`)) return;
        act(() => userAdminAction(user.username, 'reset-password'), 'Password reset initiated')();
      });
      on('.user-set-pw', () => openSetPasswordModal(user));
      on('.user-toggle-enabled', act(
        () => userAdminAction(user.username, user.enabled ? 'disable' : 'enable'),
        `${displayName(user)} ${user.enabled ? 'disabled' : 'enabled'}`));
      on('.user-delete', () => {
        if (!confirm(`Delete ${displayName(user)} permanently?\n\nThis removes the account, its tenant memberships and its alert rules. The email address can register again afterwards.`)) return;
        act(() => deleteUser(user.username), `${displayName(user)} deleted`)();
      });
    });
  }

  async function renderUsers() {
    try {
      allUsers = await loadUsers();
      populateFilterOptions();
      renderRows();
    } catch (err) {
      tbody.innerHTML = `<tr><td colspan="8" class="text-danger py-4 text-center">Failed to load users</td></tr>`;
    }
  }

  // Filter + sort wiring (filters re-render locally; no reload needed).
  container.querySelector('#uf-search').addEventListener('input', renderRows);
  ['#uf-role', '#uf-tenant', '#uf-status'].forEach(sel =>
    container.querySelector(sel).addEventListener('change', renderRows));
  container.querySelectorAll('th.u-sort').forEach(th => {
    th.addEventListener('click', () => {
      const key = th.dataset.sort;
      if (sortKey === key) {
        sortDir = sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        sortKey = key;
        // Dates start newest-first, text columns start A→Z.
        sortDir = (key === 'created' || key === 'lastSeen') ? 'desc' : 'asc';
      }
      renderRows();
    });
  });

  // Friendly display: Cognito username is the UUID (sub); show the email/local part.
  const displayName = (u) => (u.email ? u.email.split('@')[0] : (u.username || '—'));

  const esc = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  // Ordered high → low privilege. Detailed capability + scope notes.
  // NOTE: platform roles (superadmin, platformSupport) are cross-tenant; every
  // other role only applies within the tenants assigned to the user.
  const ROLE_INFO = [
    { role: 'superadmin',          label: 'Super Admin',          desc: 'Unrestricted access to the whole platform and every tenant. Manages users, roles, tenants, devices and all data. The only role that can grant or revoke Super Admin.' },
    { role: 'admin',               label: 'Admin',                desc: 'Full platform access across all tenants — the same as Super Admin, except it cannot grant or revoke the Super Admin role.' },
    { role: 'platformSupport',     label: 'Platform Support',     desc: 'Works across all tenants for support: manage tenants, users, their tenant assignments and roles (any role except Super Admin), devices and shared (public) POIs.' },
    { role: 'tenantAdministrator', label: 'Tenant Administrator', desc: 'Full control, but only within the assigned tenants: that tenant’s assets, devices, POIs and POI types, plus its users — including their tenant membership, non-platform roles and password resets. Cannot grant platform roles and has no access to other tenants.' },
    { role: 'groupAdministrator',  label: 'Group Administrator',  desc: 'Within the assigned tenants only: create and edit assets and POIs. Cannot delete, and cannot manage devices, tenants or users.' },
    { role: 'operator',            label: 'Operator',             desc: 'Within the assigned tenants only: create and edit assets and POIs. Everything else is read-only; cannot delete or manage devices, tenants or users.' },
    { role: 'powerViewer',         label: 'Power Viewer',         desc: 'Read-only within the assigned tenants, including asset detail and the full location history. Cannot create, edit or delete anything.' },
    { role: 'viewer',              label: 'Viewer',               desc: 'Read-only within the assigned tenants: view assets, POIs and the live map. No device management and no changes.' },
    { role: 'apiUser',             label: 'API User',             desc: 'Non-interactive role for programmatic/API access only (e.g. device data ingest). Has no web console permissions.' },
  ];

  // ====================== UNIFIED USER MODAL (role + tenants) ======================
  async function openUserModal(user) {
    const myGroups = currentUser.groups || [];
    const canRoles = can(myGroups, 'manageUserGroups');
    const canTenants = can(myGroups, 'manageUserTenants');

    const allRoles = ROLE_INFO.map(r => r.role);
    const current = user.groups || [];

    // Tenant-scoped role admin (manageTenantUsers, no full manageUserGroups): may
    // assign NON-platform roles, and only to NON-platform target users. The server
    // re-checks all of this on save.
    const targetIsPlatform = current.some(r => PLATFORM_ROLES.includes(r));
    const canTenantRoles = !canRoles && can(myGroups, 'manageTenantUsers') && !targetIsPlatform;

    // Grantable roles: superadmin only a superadmin may grant; a full admin gets
    // everything else; a tenant-scoped admin gets non-platform roles only.
    const isSuper = myGroups.includes('superadmin');
    const grantable = canRoles
      ? new Set(isSuper ? allRoles : allRoles.filter(r => r !== 'superadmin'))
      : new Set(allRoles.filter(r => !PLATFORM_ROLES.includes(r)));

    // Editing your own account? You must not change (strip) your own role.
    const isSelf = (currentUser.username && currentUser.username === user.username)
      || (currentUser.email && currentUser.email === user.email)
      || (currentUser.sub && currentUser.sub === user.sub);

    const currentPrimary = allRoles.find(r => current.includes(r)) || '';
    const holdsUngrantable = current.some(r => !grantable.has(r));
    const roleLocked = isSelf || holdsUngrantable;

    // ---- Role section (full role admin, or a tenant-scoped admin on a
    //      non-platform user — the grantable set above already limits the list) ----
    let roleSection = '';
    if (canRoles || canTenantRoles) {
      const options = [{ role: '', label: 'No role', desc: 'No access until a role is assigned.' }]
        .concat(ROLE_INFO.filter(r => grantable.has(r.role) || current.includes(r.role)));
      const rows = options.map(o => {
        const checked = (o.role === currentPrimary);
        return `
          <label class="list-group-item d-flex gap-2 align-items-start py-2" style="cursor:${roleLocked ? 'default' : 'pointer'}">
            <input class="form-check-input flex-shrink-0 mt-1" type="radio" name="role-choice"
                   value="${esc(o.role)}" ${checked ? 'checked' : ''} ${roleLocked ? 'disabled' : ''}>
            <span class="lh-sm">
              <span class="fw-semibold" style="font-size:.82rem">${esc(o.label)}</span>${o.role ? ` <span class="text-muted" style="font-size:.68rem">${esc(o.role)}</span>` : ''}
              <span class="d-block text-muted" style="font-size:.72rem">${esc(o.desc)}</span>
            </span>
          </label>`;
      }).join('');
      const lockNote = isSelf
        ? 'This is your own account — your role can\'t be changed here.'
        : (holdsUngrantable ? 'This user holds a role you\'re not allowed to change.' : '');
      roleSection = `
        <div class="mb-3">
          <div class="fw-semibold mb-1">Role</div>
          <p class="text-muted mb-2" style="font-size:.72rem">${roleLocked ? esc(lockNote)
            : 'Select one role. Platform roles (Super Admin, Platform Support) apply across all tenants; every other role is limited to the tenants assigned below.'}</p>
          <div class="list-group">${rows}</div>
        </div>`;
    }

    // ---- Tenants section (only if you may manage tenants) ----
    let currentTenantIds = [];
    let tenantSection = '';
    let allTenantsList = [];   // hoisted so the save handler can map id → name
    if (canTenants) {
      const allTenants = await loadTenants();
      allTenantsList = allTenants;
      try {
        const res = await window.authenticatedFetch(`api/v1/users/${encodeURIComponent(user.username)}/tenants`);
        if (res.ok) currentTenantIds = (await res.json()).tenants.map(t => t.tenantId);
      } catch { console.warn('Could not load current user tenants'); }
      tenantSection = `
        <div>
          <div class="fw-semibold mb-1">Tenants</div>
          <p class="text-muted mb-2" style="font-size:.72rem">The tenants this user belongs to. For every non-platform role above, access is limited to exactly these tenants.</p>
          <select id="tenant-select" class="form-select form-select-sm tenant-blur" multiple style="width:100%">
            ${allTenants.map(t => `<option value="${esc(t.tenantId)}">${esc(t.name || t.tenantId)}</option>`).join('')}
          </select>
        </div>`;
    }

    const nameEsc = esc(displayName(user));
    const emailEsc = user.email ? esc(user.email) : '';
    const modalHTML = `
      <div class="modal fade" id="userModal" tabindex="-1">
        <div class="modal-dialog">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title mb-0">Manage <strong>${nameEsc}</strong>${emailEsc ? `<span class="d-block text-muted fw-normal" style="font-size:.72rem">${emailEsc}</span>` : ''}</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div class="modal-body">
              ${roleSection}
              ${tenantSection}
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
              <button type="button" id="btn-save-user" class="btn btn-primary">Save</button>
            </div>
          </div>
        </div>
      </div>`;

    document.body.insertAdjacentHTML('beforeend', modalHTML);
    const modalEl = document.getElementById('userModal');
    const modal = new bootstrap.Modal(modalEl);
    modal.show();

    let $tenantSelect = null;
    if (canTenants) {
      $tenantSelect = $('#tenant-select');
      $tenantSelect.select2({ placeholder: 'Select tenants…', allowClear: true, width: '100%', dropdownParent: modalEl });
      if (currentTenantIds.length) $tenantSelect.val(currentTenantIds).trigger('change');
    }

    modalEl.addEventListener('hidden.bs.modal', () => {
      if ($tenantSelect && $tenantSelect.data('select2')) $tenantSelect.select2('destroy');
      modalEl.remove();
    }, { once: true });

    document.getElementById('btn-save-user').onclick = async () => {
      const saveBtn = document.getElementById('btn-save-user');
      saveBtn.disabled = true;
      try {
        // Collect what changed so the user gets ONE consolidated access email.
        const change = { rolesAdded: [], rolesRemoved: [], tenantsAdded: [], tenantsRemoved: [] };
        const tenantNameById = new Map(allTenantsList.map(t => [t.tenantId, t.name || t.tenantId]));

        // Role change (only if allowed, editable and actually changed).
        if ((canRoles || canTenantRoles) && !roleLocked) {
          const sel = modalEl.querySelector('input[name="role-choice"]:checked');
          const role = sel ? sel.value : '';
          const desired = role ? [role] : [];
          const changed = desired.length !== current.length || desired.some(r => !current.includes(r));
          if (changed) {
            await updateUserGroups(user.username, desired, { suppressNotify: true });
            change.rolesAdded = desired.filter(r => !current.includes(r));
            change.rolesRemoved = current.filter(r => !desired.includes(r));
          }
        }
        // Tenant diff (add newly selected, remove deselected) — suppress per-call
        // emails; we send one summary below.
        if (canTenants) {
          const selected = ($tenantSelect.val() || []);
          for (const t of selected.filter(t => !currentTenantIds.includes(t))) {
            await assignUserToTenant(user.username, t, { suppressNotify: true });
            change.tenantsAdded.push(tenantNameById.get(t) || t);
          }
          for (const t of currentTenantIds.filter(t => !selected.includes(t))) {
            await unassignUserFromTenant(user.username, t, { suppressNotify: true });
            change.tenantsRemoved.push(tenantNameById.get(t) || t);
          }
        }

        // One consolidated email if anything actually changed (best-effort).
        if (change.rolesAdded.length || change.rolesRemoved.length || change.tenantsAdded.length || change.tenantsRemoved.length) {
          await notifyAccessChange(user.username, change);
        }

        modal.hide();
        window.showToast(`Updated ${displayName(user)}`, 'success');
        await renderUsers();
      } catch (e) {
        window.showToast(e?.message || 'Failed to update user', 'danger');
        saveBtn.disabled = false;
      }
    };
  }

  // ====================== CREATE USER MODAL ======================
  // Invite a user by email with role + tenants pre-assigned, so they have full
  // access on their first login. Cognito emails a temporary password.
  async function openCreateUserModal() {
    const callerIsSuper = (currentUser.groups || []).includes('superadmin');
    const roleOptions = [{ role: '', label: 'No role' }]
      .concat(ROLE_INFO.filter(r => callerIsSuper || r.role !== 'superadmin'));

    let allTenants = [];
    try { allTenants = await loadTenants(); } catch { /* tenant select stays empty */ }

    const modalHTML = `
      <div class="modal fade" id="createUserModal" tabindex="-1">
        <div class="modal-dialog">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Add user</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div class="modal-body">
              <p class="text-muted mb-3" style="font-size:.72rem">The user receives an invitation email with a temporary password and must set their own password on first login. Role and tenants below apply immediately.</p>
              <div class="mb-3">
                <label class="form-label fw-semibold" for="cu-email">Email</label>
                <input id="cu-email" type="email" class="form-control" placeholder="name@company.com" autocomplete="off">
              </div>
              <div class="mb-3">
                <label class="form-label fw-semibold" for="cu-role">Role</label>
                <select id="cu-role" class="form-select">
                  ${roleOptions.map(o => `<option value="${esc(o.role)}">${esc(o.label)}</option>`).join('')}
                </select>
                <div id="cu-role-desc" class="form-text" style="font-size:.72rem"></div>
              </div>
              <div>
                <label class="form-label fw-semibold" for="cu-tenants">Tenants</label>
                <select id="cu-tenants" class="form-select form-select-sm tenant-blur" multiple style="width:100%">
                  ${allTenants.map(t => `<option value="${esc(t.tenantId)}">${esc(t.name || t.tenantId)}</option>`).join('')}
                </select>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
              <button type="button" id="btn-create-user" class="btn btn-primary">Create &amp; invite</button>
            </div>
          </div>
        </div>
      </div>`;

    document.body.insertAdjacentHTML('beforeend', modalHTML);
    const modalEl = document.getElementById('createUserModal');
    const modal = new bootstrap.Modal(modalEl);
    modal.show();

    const $tenantSelect = $('#cu-tenants');
    $tenantSelect.select2({ placeholder: 'Select tenants…', allowClear: true, width: '100%', dropdownParent: modalEl });

    // Live role description under the select.
    const roleSel = modalEl.querySelector('#cu-role');
    const roleDesc = modalEl.querySelector('#cu-role-desc');
    const updateDesc = () => {
      const info = ROLE_INFO.find(r => r.role === roleSel.value);
      roleDesc.textContent = info ? info.desc : '';
    };
    roleSel.addEventListener('change', updateDesc);
    updateDesc();

    modalEl.addEventListener('hidden.bs.modal', () => {
      if ($tenantSelect.data('select2')) $tenantSelect.select2('destroy');
      modalEl.remove();
    }, { once: true });

    document.getElementById('btn-create-user').onclick = async () => {
      const email = modalEl.querySelector('#cu-email').value.trim();
      if (!email || !email.includes('@')) {
        window.showToast('Please enter a valid email address', 'warning');
        return;
      }
      const btn = document.getElementById('btn-create-user');
      btn.disabled = true;
      try {
        await createUser({ email, role: roleSel.value || null, tenants: $tenantSelect.val() || [] });
        modal.hide();
        window.showToast(`Invitation sent to ${email}`, 'success');
        await renderUsers();
      } catch (e) {
        window.showToast(e?.message || 'Failed to create user', 'danger');
        btn.disabled = false;
      }
    };
  }

  // ====================== SET PASSWORD / ACTIVATE MODAL ======================
  // Set a password directly (no email round-trip) — the escape hatch when
  // invitation/reset emails don't arrive. Permanent = account becomes CONFIRMED
  // immediately; temporary = user must change it at next login.
  function genPassword() {
    const U = 'ABCDEFGHJKLMNPQRSTUVWXYZ', L = 'abcdefghijkmnpqrstuvwxyz', D = '23456789', S = '!@#$%*?';
    const all = U + L + D + S;
    const pick = (set) => set[Math.floor(Math.random() * set.length)];
    let pw = [pick(U), pick(L), pick(D), pick(S)];
    for (let i = 0; i < 10; i++) pw.push(pick(all));
    // Shuffle so the guaranteed classes aren't always in front.
    for (let i = pw.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [pw[i], pw[j]] = [pw[j], pw[i]]; }
    return pw.join('');
  }

  function openSetPasswordModal(user) {
    const modalHTML = `
      <div class="modal fade" id="setPwModal" tabindex="-1">
        <div class="modal-dialog">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title mb-0">Set password — <strong>${esc(displayName(user))}</strong>
                ${user.email ? `<span class="d-block text-muted fw-normal" style="font-size:.72rem">${esc(user.email)}</span>` : ''}</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div class="modal-body">
              <p class="text-muted mb-3" style="font-size:.72rem">Sets a password directly — no email is sent. Hand it to the user over a secure channel. Use this when invitation/reset emails don't arrive.</p>
              <label class="form-label fw-semibold" for="sp-pw">Password</label>
              <div class="input-group mb-2">
                <input id="sp-pw" type="text" class="form-control font-monospace" value="${esc(genPassword())}" autocomplete="off" spellcheck="false">
                <button class="btn btn-outline-secondary" type="button" id="sp-regen" title="Generate a new password"><i class="fa-solid fa-arrows-rotate"></i></button>
                <button class="btn btn-outline-secondary" type="button" id="sp-copy" title="Copy"><i class="fa-solid fa-copy"></i></button>
              </div>
              <div class="form-check">
                <input class="form-check-input" type="checkbox" id="sp-temp">
                <label class="form-check-label" for="sp-temp" style="font-size:.8rem">
                  Require change at next login <span class="text-muted">(temporary — account stays “force change password” until they set their own)</span>
                </label>
              </div>
              <div class="form-text" style="font-size:.72rem">Unchecked: the account is activated immediately (status becomes <b>CONFIRMED</b>).</div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
              <button type="button" id="btn-set-pw" class="btn btn-primary">Set password</button>
            </div>
          </div>
        </div>
      </div>`;
    document.body.insertAdjacentHTML('beforeend', modalHTML);
    const modalEl = document.getElementById('setPwModal');
    const modal = new bootstrap.Modal(modalEl);
    modal.show();
    modalEl.addEventListener('hidden.bs.modal', () => modalEl.remove(), { once: true });

    const pwInput = modalEl.querySelector('#sp-pw');
    modalEl.querySelector('#sp-regen').onclick = () => { pwInput.value = genPassword(); };
    modalEl.querySelector('#sp-copy').onclick = async () => {
      try { await navigator.clipboard.writeText(pwInput.value); window.showToast('Password copied', 'success'); }
      catch { pwInput.select(); }
    };

    document.getElementById('btn-set-pw').onclick = async () => {
      const password = pwInput.value.trim();
      const permanent = !modalEl.querySelector('#sp-temp').checked;
      if (password.length < 8) { window.showToast('Password must be at least 8 characters', 'warning'); return; }
      const btn = document.getElementById('btn-set-pw');
      btn.disabled = true;
      try {
        await userAdminAction(user.username, 'set-password', { password, permanent });
        modal.hide();
        // Surface the password once more so the admin can still copy it after closing.
        window.showToast(`Password set for ${displayName(user)}${permanent ? ' — account active' : ' (must change at next login)'}. Password: ${password}`, 'success');
        await renderUsers();
      } catch (e) {
        window.showToast(e?.message || 'Failed to set password', 'danger');
        btn.disabled = false;
      }
    };
  }

  const addBtn = document.getElementById('btn-add-user');
  if (can(currentUser.groups || [], 'manageUserGroups')) {
    addBtn.classList.remove('d-none');
    addBtn.onclick = openCreateUserModal;
  }

  document.getElementById('btn-refresh-users').onclick = renderUsers;
  await renderUsers();
}